import { Box, Grid, Divider, IconButton } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  isCranial,
  isSpine,
  showApproach,
  showMinimallyInvasive,
  showNavigation,
  showRobotic,
  showSide,
  showTarget,
  showOtherVendors,
  showOutcome,
  showAccess,
  showComplications,
  showDecompressionLevels,
  showDurationOfProcedure,
  showDurationOfRadiation,
  showExtensionToPelvis,
  showFindings,
  showFusion,
  showFusionLevels,
  showImplant,
  showImplantType,
  showInterbodyFusion,
  showInterLocation,
  showListIndication,
  showMorphogenicProtein,
  showBoneGraft,
  showNerve,
  showNeuroLocation,
  showOsteotomy,
  showProcedure,
  showTextIndication,
  showTextLocation,
  showTiciGrade,
  showVascularClosureDevice,
  isAbscess,
  isAneurysm,
  isAVF,
  isAVM,
  isBedside,
  isBleeding,
  isDissection,
  isFracture,
  isFunctional,
  isHematoma,
  isHydrocephalus,
  isInter,
  isInterventional,
  isNeurosurgery,
  isPain,
  isPeripheral,
  isRadiology,
  isRadiosurgery,
  isStroke,
  isTumor,
  isVasospasm,
} from 'common/caseHelpers';
import CustomDatePicker from 'components/inputs/CustomDatePicker/CustomDatePicker';
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect';
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput';
import CustomFormMultiSelect from 'components/inputs/CustomFormMultiSelect/CustomFormMultiSelect';
import { procedureDataType, caseSharedProps } from 'types/types';
import { toFieldError } from 'pages/case/AddCasePage/ProcedureDetails';
import CustomText from 'components/common/CustomText/CustomText';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import DeleteIcon from 'icons/DeleteIcon';
import { useState } from 'react';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomDialog from 'components/dialogs/CustomDialog';
import { snackbarService } from 'services/snackbarService';
import { useDeleteCaseProcedure } from 'hooks/useProcedure';
import { useAuth } from 'context/AuthContext';

interface RowProps {
  index: number;
  remove: (index: number) => void;
  disableRemove: boolean;
  shared: caseSharedProps;
}

export function CaseProcedureRow({
  index,
  remove,
  disableRemove,
  shared,
}: RowProps) {
  const { user } = useAuth();
  const globalStyles = useGlobalStyles();
  const [open, setOpen] = useState<boolean>(false);

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<procedureDataType>();

  const base = `case_procedures.${index}` as const;

  const values = useWatch({
    control,
    name: base,
  }) as procedureDataType['case_procedures'][number];

  const procedureType = values?.type;
  const indication = values?.indication.toString();

  const {
    procedureTypes,
    cranialApproachList,
    spineApproachList,
    interventionalIndicationList,
    radiologyIndicationList,
    cranialIndicationList,
    spineIndicationList,
    functionalIndicationList,
    nervesIndicationList,
    interIndicationList,
    speciality = '',
    spineLocationList,
    strokeLocationList,
    interventionalProcedureTypeList,
    aneurysmLocationList,
    hematomaLocationList,
    fractureLocationList,
    hydrocephalusLocationList,
    avmLocationList,
    bleedingLocationList,
    aneurysmLocationInterList,
    dissectionLocationList,
    tumorLocationList,
    bedsideProcedureTypeList,
    yesNoList = [],
    isMultipleSelection,
    sideList,
    ticiGradeList,
    osteotomyList,
    vendorsList,
    positionList,
  } = shared;

  const approachList =
    isCranial(procedureType)
      ? cranialApproachList
      : isSpine(procedureType)
        ? spineApproachList
        : [];

  const indicationList =
    isInterventional(speciality)
      ? interventionalIndicationList
      : isRadiology(speciality)
        ? radiologyIndicationList
        : isCranial(procedureType)
          ? cranialIndicationList
          : isSpine(procedureType)
            ? spineIndicationList
            : isFunctional(procedureType)
              ? functionalIndicationList
              : isPeripheral(procedureType)
                ? nervesIndicationList
                : interIndicationList;


  const procedureList =
    (isInterventional(speciality) ||
      (isNeurosurgery(speciality) && isInter(procedureType)))
      ? interventionalProcedureTypeList
      : isNeurosurgery(speciality) &&
        (isBedside(procedureType) ||
          isPain(procedureType) ||
          isRadiosurgery(procedureType))
        ? bedsideProcedureTypeList
        : [];

  const neuroLocationList =
    isCranial(procedureType) &&
      (isTumor(indication) ||
        isAVM(indication) ||
        isAVF(indication) ||
        isAbscess(indication))
      ? tumorLocationList
      : isCranial(procedureType) && isAneurysm(indication)
        ? aneurysmLocationList
        : isCranial(procedureType) && isHematoma(indication)
          ? hematomaLocationList
          : isCranial(procedureType) && isFracture(indication)
            ? fractureLocationList
            : isCranial(procedureType) && isHydrocephalus(indication)
              ? hydrocephalusLocationList
              : isSpine(procedureType)
                ? spineLocationList
                : isInter(procedureType) && (isAVM(indication) || isAVF(indication))
                  ? avmLocationList
                  : isInter(procedureType) && isBleeding(indication)
                    ? bleedingLocationList
                    : isInter(procedureType) && isAneurysm(indication)
                      ? aneurysmLocationInterList
                      : isStroke(indication)
                        ? strokeLocationList
                        : isInter(procedureType) &&
                          (isDissection(indication) ||
                            isVasospasm(indication) ||
                            isStroke(indication))
                          ? dissectionLocationList
                          : [];

  const interLocationList =
    isAVM(indication) || isAVF(indication)
      ? avmLocationList
      : isBleeding(indication)
        ? bleedingLocationList
        : isAneurysm(indication)
          ? aneurysmLocationInterList
          : isStroke(indication)
            ? strokeLocationList
            : isDissection(indication) || isVasospasm(indication)
              ? dissectionLocationList
              : [];

  const { mutate: deleteMutation, isLoading: isDeleting } = useDeleteCaseProcedure(
    (data: any) => {
      snackbarService.showSnackbar('Case procedure has been deleted successfully', 'success');
      remove(index);
    },
    (error: any) => snackbarService.showSnackbar(error?.message, 'error')
  )

  const handleRemoveProcedure = () => {
    remove(index)
  }

  const handleDeleteProcedure = () => {
    deleteMutation({
      userId: user.id,
      procedureId: Number(values.procedure),
      caseProcedureId: Number(values.id),
    });
  }

  return (
    <>
      <Box className={globalStyles.sectionContainer}>
        <Box className={globalStyles.sectionHeader} justifyContent='space-between'>
          <Box display='flex'>
            <CustomText
              text={`Procedure ${index + 2}`}
              color={colors.primary}
              fontWeight={600}
            />
            {!values?.id && (
              <CustomText text="New" fontSize="10px" color="red" ml={0.1} />
            )}
          </Box>
          <Box mr={2}>
            <IconButton onClick={() => setOpen(true)}>
              <DeleteIcon color={colors.error} size={16} />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        <Grid container p={3} spacing={1.5}>

          {/* Date of procedure */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomDatePicker
              name={`case_procedures.${index}.date_of_procedure`}
              label="Date of Procedure"
              required={true}
              control={control}
              placeholder="Date of procedure"
            />
          </Grid>

          {/* Procedure name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name={`case_procedures.${index}.procedure_name`}
              label="Procedure Name"
              required={true}
              placeholder="Procedure name"
              register={register}
              error={toFieldError(errors?.case_procedures?.[index]?.procedure_name)}
            />
          </Grid>

          {/* Procedure type */}
          <Grid size={{ xs: 12, md: 6 }}>
            {procedureTypes?.length ? (
              <CustomFormSelect
                name={`case_procedures.${index}.type`}
                label="Procedure Type"
                required={true}
                placeholder="Procedure type"
                error={toFieldError(errors?.case_procedures?.[index]?.type)}
                options={procedureTypes || []}
              />
            ) : (
              <CustomFormTextInput
                name={`case_procedures.${index}.type`}
                label="Procedure Type"
                required={true}
                placeholder="Procedure type"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.type)}
              />
            )}
          </Grid>

          {/* Approach */}
          {showApproach(values) && (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormMultiSelect
                name={`case_procedures.${index}.approach`}
                label="Approach"
                required={false}
                placeholder="Select approach"
                control={control}
                error={toFieldError(errors?.case_procedures?.[index]?.approach)}
                options={approachList || []}
              />
            </Grid>
          )}

          {/* Position */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormMultiSelect
              name={`case_procedures.${index}.position`}
              label="Position"
              required={false}
              placeholder="Select position"
              control={control}
              error={toFieldError(errors?.case_procedures?.[index]?.position)}
              options={positionList || []}
            />
          </Grid>

          {/* Minimally invasive */}
          {showMinimallyInvasive(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.minimally_invasive`}
                label="Minimally Invasive"
                required={false}
                placeholder="Select option"
                error={toFieldError(errors?.case_procedures?.[index]?.minimally_invasive)}
                options={yesNoList || []}
              />
            </Grid>
          ) : null}

          {/* Navigation */}
          {showNavigation(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.navigation`}
                label="Navigation"
                required={false}
                placeholder="Select option"
                error={toFieldError(errors?.case_procedures?.[index]?.navigation)}
                options={yesNoList || []}
              />
            </Grid>
          ) : null}

          {/* Robotic */}
          {showRobotic(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.robotic`}
                label="Robotic"
                required={false}
                placeholder="Select option"
                error={toFieldError(errors?.case_procedures?.[index]?.robotic)}
                options={yesNoList || []}
              />
            </Grid>
          ) : null}

          {/* Indication */}
          {showTextIndication(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.indication`}
                label="Indication"
                required={false}
                placeholder="Indication"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.indication)}
              />
            </Grid>
          ) : showListIndication(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              {isMultipleSelection ? (
                <CustomFormMultiSelect
                  name={`case_procedures.${index}.indication`}
                  label="Indication"
                  required={false}
                  placeholder="Select indication"
                  control={control}
                  error={toFieldError(errors?.case_procedures?.[index]?.indication)}
                  options={indicationList || []}
                />
              ) : (
                <CustomFormSelect
                  name={`case_procedures.${index}.indication`}
                  label="Indication"
                  required={false}
                  placeholder="Select indication"
                  error={toFieldError(errors?.case_procedures?.[index]?.indication)}
                  options={indicationList || []}
                />
              )}
            </Grid>
          ) : null}

          {/* Side */}
          {showSide(speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.side`}
                label="Side"
                required={false}
                placeholder="Select side"
                error={toFieldError(errors?.case_procedures?.[index]?.side)}
                options={sideList || []}
              />
            </Grid>
          ) : null}

          {/* Target */}
          {showTarget(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.target`}
                label="Target"
                required={false}
                placeholder="Target"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.target)}
              />
            </Grid>
          ) : null}

          {/* Nerve */}
          {showNerve(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.nerve`}
                label="Nerve"
                required={false}
                placeholder="Nerve"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.nerve)}
              />
            </Grid>
          ) : null}

          {/* Text Location */}
          {showTextLocation(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.location`}
                label="Location"
                required={false}
                placeholder="Location"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.location)}
              />
            </Grid>
          ) : null}

          {/* Neuro Location */}
          {showNeuroLocation(values, speciality) && neuroLocationList?.length ? (
            <Grid size={{ xs: 12, md: 6 }}>
              {isMultipleSelection ? (
                <CustomFormMultiSelect
                  name={`case_procedures.${index}.location`}
                  label="Location"
                  required={false}
                  placeholder="Select location"
                  control={control}
                  options={neuroLocationList || []}
                  error={toFieldError(errors?.case_procedures?.[index]?.location)}
                />
              ) : (
                <CustomFormSelect
                  name={`case_procedures.${index}.location`}
                  label="Location"
                  required={false}
                  placeholder="Select location"
                  options={neuroLocationList || []}
                  error={toFieldError(errors?.case_procedures?.[index]?.location)}
                />
              )}
            </Grid>
          ) : null}

          {/* Inter Location */}
          {showInterLocation(values, speciality) && interLocationList?.length ? (
            <Grid size={{ xs: 12, md: 6 }}>
              {isMultipleSelection ? (
                <CustomFormMultiSelect
                  name={`case_procedures.${index}.location`}
                  label="Location"
                  required={false}
                  placeholder="Select location"
                  control={control}
                  options={interLocationList || []}
                  error={toFieldError(errors?.case_procedures?.[index]?.location)}
                />
              ) : (
                <CustomFormSelect
                  name={`case_procedures.${index}.location`}
                  label="Location"
                  required={false}
                  placeholder="Select location"
                  options={interLocationList || []}
                  error={toFieldError(errors?.case_procedures?.[index]?.location)}
                />
              )}
            </Grid>
          ) : null}

          {/* Procedure */}
          {showProcedure(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              {isMultipleSelection ? (
                <CustomFormMultiSelect
                  name={`case_procedures.${index}._procedure`}
                  label="Procedure"
                  required={false}
                  placeholder="Select procedure"
                  control={control}
                  options={procedureList || []}
                  error={toFieldError(errors?.case_procedures?.[index]?._procedure)}
                />
              ) : (
                <CustomFormSelect
                  name={`case_procedures.${index}._procedure`}
                  label="Procedure"
                  required={false}
                  placeholder="Select procedure"
                  options={procedureList || []}
                  error={toFieldError(errors?.case_procedures?.[index]?._procedure)}
                />
              )}
            </Grid>
          ) : null}

          {/* Tici grade */}
          {showTiciGrade(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.tici_grade`}
                label="TICI Grade"
                required={false}
                placeholder="Select tici grade"
                options={ticiGradeList || []}
                error={toFieldError(errors?.case_procedures?.[index]?.tici_grade)}
              />
            </Grid>
          ) : null}

          {/* Osteotomy */}
          {showOsteotomy(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.osteotomy`}
                label="Osteotomy"
                required={false}
                placeholder="Select osteotomy"
                options={osteotomyList || []}
                error={toFieldError(errors?.case_procedures?.[index]?.osteotomy)}
              />
            </Grid>
          ) : null}

          {/* Fusion */}
          {showFusion(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}._fusion`}
                label="Fusion"
                required={false}
                placeholder="Select option"
                options={yesNoList}
                error={toFieldError(errors?.case_procedures?.[index]?._fusion)}
              />
            </Grid>
          ) : null}

          {/* Interbody fusion */}
          {showInterbodyFusion(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.interbody_fusion`}
                label="Interbody Fusion"
                required={false}
                placeholder="Select option"
                options={yesNoList}
                error={toFieldError(errors?.case_procedures?.[index]?.interbody_fusion)}
              />
            </Grid>
          ) : null}

          {/* Extension to pelvis */}
          {showExtensionToPelvis(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.extension_to_pelvis`}
                label="Extension to Pelvis"
                required={false}
                placeholder="Select option"
                options={yesNoList}
                error={toFieldError(errors?.case_procedures?.[index]?.extension_to_pelvis)}
              />
            </Grid>
          ) : null}

          {/* Fusion levels */}
          {showFusionLevels(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.fusion_levels`}
                label="Fusion Levels"
                required={false}
                placeholder="Fusion Levels"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.fusion_levels)}
              />
            </Grid>
          ) : null}

          {/* Decompression levels */}
          {showDecompressionLevels(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.decompression_levels`}
                label="Decompression Levels"
                required={false}
                placeholder="Decompression Levels"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.decompression_levels)}
              />
            </Grid>
          ) : null}

          {/* Morphogenic protein */}
          {showMorphogenicProtein(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.morphogenic_protein`}
                label="Morphogenic Protein"
                required={false}
                placeholder="Select option"
                options={yesNoList}
                error={toFieldError(errors?.case_procedures?.[index]?.morphogenic_protein)}
              />
            </Grid>
          ) : null}

          {/* Bone Graft */}
          {showBoneGraft(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.bone_graft`}
                label="Bone Graft"
                required={false}
                placeholder="Select option"
                options={yesNoList}
                error={toFieldError(errors?.case_procedures?.[index]?.bone_graft)}
              />
            </Grid>
          ) : null}

          {/* Implant */}
          {showImplant(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormSelect
                name={`case_procedures.${index}.implant`}
                label="Implant"
                required={false}
                placeholder="Select option"
                options={yesNoList}
                error={toFieldError(errors?.case_procedures?.[index]?.implant)}
              />
            </Grid>
          ) : null}

          {/* Implant type */}
          {showImplantType(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.implant_type`}
                label="Implant Type"
                required={false}
                placeholder="Implant Type"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.implant_type)}
              />
            </Grid>
          ) : null}

          {/* Vendors */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormMultiSelect
              name={`case_procedures.${index}._vendors`}
              label="Vendors"
              required={false}
              placeholder="Select vendors"
              control={control}
              options={vendorsList || []}
              error={toFieldError(errors?.case_procedures?.[index]?._vendors)}
            />
          </Grid>

          {/* Other vendors */}
          {showOtherVendors(values) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.vendors`}
                label="Other Vendors"
                required={false}
                placeholder="Other vendors"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.vendors)}
              />
            </Grid>
          ) : null}

          {/* Access */}
          {showAccess(speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.access`}
                label="Access"
                required={false}
                placeholder="Access"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.access)}
              />
            </Grid>
          ) : null}

          {/* Vascular closure device */}
          {showVascularClosureDevice(speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.vascular_closure_device`}
                label="Vascular Closure Device"
                required={false}
                placeholder="Vascular closure device (if used)"
                register={register}
                error={toFieldError(
                  errors?.case_procedures?.[index]?.vascular_closure_device
                )}
              />
            </Grid>
          ) : null}

          {/* Duration of Radiation */}
          {showDurationOfRadiation(values, speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.duration_of_radiation`}
                label="Duration of Radiation"
                required={false}
                placeholder="Duration of Radiation"
                register={register}
                error={toFieldError(
                  errors?.case_procedures?.[index]?.duration_of_radiation
                )}
              />
            </Grid>
          ) : null}

          {/* Duration of Procedure */}
          {showDurationOfProcedure(speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.duration_of_procedure`}
                label="Duration of Procedure"
                required={false}
                placeholder="Duration of Procedure"
                register={register}
                error={toFieldError(
                  errors?.case_procedures?.[index]?.duration_of_procedure
                )}
              />
            </Grid>
          ) : null}

          {/* Findings */}
          {showFindings(speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.findings`}
                label="Findings"
                required={false}
                placeholder="Findings"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.findings)}
              />
            </Grid>
          ) : null}

          {/* Complications */}
          {showComplications(speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.complications`}
                label="Complications"
                required={false}
                placeholder="Complications"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.complications)}
              />
            </Grid>
          ) : null}

          {/* Outcome */}
          {showOutcome(speciality) ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={`case_procedures.${index}.outcome`}
                label="Outcome"
                required={false}
                placeholder="Outcome"
                register={register}
                error={toFieldError(errors?.case_procedures?.[index]?.outcome)}
              />
            </Grid>
          ) : null}

        </Grid>

      </Box>

      {/* Remove / Delete Dialog */}
      <CustomDialog
        open={open}
        title={values?.id ? "Delete Procedure" : "Remove Procedure"}
        message={values?.id
          ? "Note: Deleting this procedure is permanent and cannot be undone."
          : "Are you sure you want to remove this procedure?"}
        buttons={[
          <CustomButton
            key={'cancel'}
            variant='outlined'
            text='Cancel'
            textColor='error'
            onClick={() => setOpen(false)}
            height={35}
          />,
          <CustomButton
            key={'delete'}
            variant='filled'
            color='error'
            text={values?.id ? "Delete Anyway" : "Remove"}
            onClick={() => (values?.id ? handleDeleteProcedure() : handleRemoveProcedure())}
            height={35}
            loading={isDeleting}
          />,
        ]}
      />

    </>
  );
}
